import {
  Table as MuiTable,
  TableBody as MuiTableBody,
  TableCell as MuiTableCell,
  TableContainer as MuiTableContainer,
  TableHead as MuiTableHead,
  TableRow as MuiTableRow,
  Paper,
  TableContainerProps,
  TableRowProps,
} from '@mui/material';
import React from 'react';
import styled, { css } from 'styled-components';
import {
  BACKGROUND,
  BLACK2,
  BORDER_GRAY,
  HOVER,
  ICON_SECONDARY,
  WHITE,
} from '../../../theme';
import Text, { TextColor, TextKind } from '../Text';
import { UnstyledButton } from '../UnstyledButton';

const TableComponent: React.FC<TableContainerProps> = ({
  children,
  ...rest
}) => (
  <MuiTableContainer component={Paper} {...rest}>
    {children}
  </MuiTableContainer>
);

export const TableContainer = styled(TableComponent)`
  && {
    position: relative;
    box-shadow: none;

    padding: 0;
    margin-bottom: 16px;

    border: 1px solid ${BORDER_GRAY};
    border-radius: 8px;

    background-color: ${BACKGROUND};
  }
`;

export const Table: React.FC<React.PropsWithChildren> = styled(MuiTable)``;

export const TableHead = styled(MuiTableHead)`
  background-color: ${WHITE};
`;

export const TableBody = styled(MuiTableBody)`
  background-color: ${BACKGROUND};
`;

export const TableRow: React.FC<TableRowProps> = (props) => (
  <MuiTableRow data-testid="tableRow" {...props} />
);

export const TableHoverRow = styled(TableRow)<{ $isHighlighted?: boolean }>`
  cursor: pointer;

  &:last-child td {
    border: none;
  }

  background-color: ${(props) => {
    if (props.$isHighlighted === undefined) {
      return `${WHITE};`;
    }

    return `${props.$isHighlighted ? WHITE : BACKGROUND};`;
  }};
  &:hover {
    background-color: ${HOVER};

    // Defined here to display the chat bubble button on row hover
    // See src/component/MessageCell.tsx
    .chat-hover {
      opacity: 1;
      background-color: ${HOVER};
    }
  }
`;

export const TableHeaderCell = styled(MuiTableCell)<{
  $removeLeftPadding?: boolean;
  $secondary?: boolean;
}>`
  color: ${ICON_SECONDARY};

  && {
    opacity: 1;
    position: sticky;
    background-color: ${WHITE};
    z-index: 1;
    top: 0;

    ${({ $secondary }) =>
      $secondary &&
      `
      border-top: 1px solid ${BORDER_GRAY};
      padding: 8px;
      padding-left: 16px;
    `};
    ${({ $removeLeftPadding }) =>
      $removeLeftPadding &&
      `
      padding-left: 0;
    `};
  }
`;

export const TableHeaderCellSortable = styled(UnstyledButton)`
  cursor: pointer;

  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;

  &:hover > p {
    color: ${BLACK2}!important;
  }

  & > svg {
    padding: 2px;
    border-radius: 99ch;
    color: ${ICON_SECONDARY};
    height: 16px;
    width: 16px;
  }

  &:hover > svg {
    background: ${HOVER};
    color: ${BLACK2};
  }
`;

export const TableHeaderCellText = ({
  isActive,
  children,
  testId,
  secondary,
}: {
  isActive?: boolean;
  children: React.ReactNode;
  testId?: string;
  secondary?: boolean;
}) => (
  <Text
    kind={secondary ? TextKind.LabelM : TextKind.LabelL}
    color={isActive ? TextColor.Primary : TextColor.Secondary}
    testId={testId}
  >
    {children}
  </Text>
);

export const TableCell = styled(MuiTableCell)<{
  $removeLeftPadding?: boolean;
  $removeRightPadding?: boolean;
  $removePadding?: boolean;
  $paddingOverride?: string;
}>`
  position: relative;
  && {
    ${({ $removePadding }) =>
      $removePadding &&
      css`
        padding: 0;
      `}

    ${({ $paddingOverride }) =>
      $paddingOverride &&
      css`
        padding: ${$paddingOverride};
      `}


    ${({ $removeRightPadding }) =>
      $removeRightPadding &&
      css`
        padding-right: 0;
      `}

    ${({ $removeLeftPadding }) =>
      $removeLeftPadding &&
      css`
        padding-left: 0;
      `}
  }
`;

export const TableEmptyState = styled.div`
  padding-top: 100px;
  padding-bottom: 100px;
  background-color: ${WHITE};
  text-align: center;
  display: flex;
  flex-direction: column;
  gap: 8px;
  align-items: center;
`;
